import { useId } from 'react';
import './aiNavIcon.css';

/**
 * Sidebar mark of «دستیار هوشمند» (a "text A + sparkle" glyph, 24×24 filled
 * paths, supplied as `artificial-intelligence_2.svg`).
 *
 * It is painted with a wide gradient that slowly slides through five
 * translucent colors - purple, green, blue, yellow, green - and loops without
 * a seam, so the entry draws the eye without changing its place, label or
 * order in the sidebar. The glyph is a mask over the sliding gradient, so the
 * colors never leak outside it. `prefers-reduced-motion` keeps the gradient
 * but stops the slide. Decorative: the nav item's label names the entry.
 */
const GLYPH =
  'm19.026,12v6c0,.552-.448,1-1,1s-1-.448-1-1v-6c0-.552.448-1,1-1s1,.448,1,1Zm-7.42-5.283l3.071,11.029c.175.63-.298,1.254-.953,1.254-.443,0-.831-.294-.952-.72l-.643-2.28h-5.206l-.643,2.28c-.12.426-.509.72-.952.72h0c-.654,0-1.128-.624-.953-1.254l3.091-11.108c.141-.608.541-1.12,1.098-1.405.568-.292,1.22-.31,1.839-.05.587.246,1.037.817,1.204,1.535Zm-.041,7.283l-1.929-6.835c-.029-.114-.191-.114-.219,0l-1.929,6.835h4.077Zm11.462-4c-.552,0-1,.448-1,1v8c0,1.654-1.346,3-3,3H5.026c-1.654,0-3-1.346-3-3V5c0-1.654,1.346-3,3-3h8c.552,0,1-.448,1-1S13.578,0,13.026,0H5.026C2.269,0,.026,2.243.026,5v14c0,2.757,2.243,5,5,5h14c2.757,0,5-2.243,5-5v-8c0-.552-.448-1-1-1Zm-6.85-4.82l1.868.787.745,1.865c.161.404.552.668.987.668s.825-.265.987-.668l.741-1.854,1.854-.741c.404-.161.668-.552.668-.987s-.265-.825-.668-.987l-1.854-.741-.741-1.854C20.601.265,20.21,0,19.776,0s-.825.265-.987.668l-.737,1.843-1.84.697c-.406.154-.678.54-.686.974-.008.435.25.83.65.999Z';

/** One full cycle of the gradient, in SVG user units (the slide moves by exactly this much). */
const PERIOD = 48;

const COLORS = [
  'rgba(168, 85, 247, 0.92)', // purple
  'rgba(52, 211, 153, 0.92)', // green
  'rgba(56, 189, 248, 0.92)', // blue
  'rgba(250, 204, 21, 0.92)', // yellow
  'rgba(74, 222, 128, 0.92)', // green
] as const;

export function AiNavIcon() {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const maskId = `ai-nav-mask-${uid}`;
  const gradientId = `ai-nav-grad-${uid}`;
  // Two periods wide (offsets i/10 and (5+i)/10) so sliding by exactly one period loops seamlessly.
  return (
    <svg className="ai-nav-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
          <path d={GLYPH} fill="#fff" />
        </mask>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2={PERIOD * 2}
          y2="0"
          gradientTransform="rotate(28 24 12)"
        >
          {[...COLORS, ...COLORS].map((color, index) => (
            <stop key={index} offset={index / 10} stopColor={color} />
          ))}
          <stop offset="1" stopColor={COLORS[0]} />
        </linearGradient>
      </defs>
      <g mask={`url(#${maskId})`}>
        <rect
          className="ai-nav-icon__flow"
          x="-24"
          y="-24"
          width={PERIOD * 2 + 48}
          height="72"
          fill={`url(#${gradientId})`}
        />
      </g>
    </svg>
  );
}
